import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import type { AdElement, Animation } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';

import { CommandRegistry } from '../../canvas/commandRegistry';
import { commitDragGesture } from '../../canvas/dragCommands';
import {
  beginDrag,
  cancelDrag,
  previewDragPosition,
  updateDragFromScreenDelta,
  type DragDraft,
} from '../../canvas/gesture';
import {
  applyDocumentLayoutToAdElements,
  documentFromEditorElements,
} from '../../canvas/layoutBridge';
import { combinedAxisAlignedBounds, pickTopmostAtPoint } from '../../canvas/selection';
import {
  fitFrameToViewport,
  recenterViewportPreservingZoom,
  screenToStage,
  type ViewportState,
} from '../../canvas/viewport';
import { toHitTargets } from './toHitTargets';

export type UseEditorCanvasSessionArgs = {
  elements: AdElement[];
  resolvedElements: AdElement[];
  animationOverrides?: ReadonlyMap<string, ElementAnimationStyle> | null;
  animation?: Animation | null;
  frame: { width: number; height: number };
  stageSize: { width: number; height: number };
  selectedElementId: string | null;
  onSelectElement: (elementId: string | null) => void;
  onElementsChange: (elements: AdElement[]) => void;
  clipToFrame?: boolean;
};

export type EditorCanvasSession = {
  stageRef: React.RefObject<HTMLDivElement>;
  viewport: ViewportState;
  displayArtwork: AdElement[];
  selectionBounds: ReturnType<typeof combinedAxisAlignedBounds>;
  undoDepth: number;
  redoDepth: number;
  clipToFrame: boolean;
  setZoom: (zoom: number) => void;
  resetViewport: () => void;
  panByScreen: (delta: { x: number; y: number }) => void;
  undo: () => void;
  redo: () => void;
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  onPointerCancel: (event: ReactPointerEvent) => void;
};

export function useEditorCanvasSession({
  elements,
  resolvedElements,
  animation = null,
  frame,
  stageSize,
  selectedElementId,
  onSelectElement,
  onElementsChange,
  clipToFrame = false,
}: UseEditorCanvasSessionArgs): EditorCanvasSession {
  const stageRef = useRef<HTMLDivElement>(null);
  const registryRef = useRef(new CommandRegistry());
  const draftRef = useRef<DragDraft | null>(null);
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const pointerSessionRef = useRef<{
    pointerId: number;
    lastClientX: number;
    lastClientY: number;
  } | null>(null);

  const [viewport, setViewport] = useState<ViewportState>(() =>
    fitFrameToViewport(frame, stageSize, 24),
  );
  const [draft, setDraft] = useState<DragDraft | null>(null);
  const [undoDepth, setUndoDepth] = useState(0);
  const [redoDepth, setRedoDepth] = useState(0);

  const viewportPadding = 24;
  const frameWidth = frame.width;
  const frameHeight = frame.height;
  const stageWidth = stageSize.width;
  const stageHeight = stageSize.height;

  const layoutRef = useRef({
    frameWidth,
    frameHeight,
    stageWidth,
    stageHeight,
  });

  useEffect(() => {
    const previous = layoutRef.current;
    const frameChanged = previous.frameWidth !== frameWidth || previous.frameHeight !== frameHeight;
    const stageChanged = previous.stageWidth !== stageWidth || previous.stageHeight !== stageHeight;
    layoutRef.current = { frameWidth, frameHeight, stageWidth, stageHeight };

    const frame = { width: frameWidth, height: frameHeight };
    const stage = { width: stageWidth, height: stageHeight };

    if (frameChanged) {
      setViewport(fitFrameToViewport(frame, stage, viewportPadding));
      return;
    }

    if (stageChanged) {
      setViewport((current) =>
        recenterViewportPreservingZoom(frame, stage, current.zoom, viewportPadding),
      );
    }
  }, [frameWidth, frameHeight, stageWidth, stageHeight, viewportPadding]);

  const document = useMemo(
    () => documentFromEditorElements(elements, animation ?? null),
    [animation, elements],
  );

  const displayArtwork = useMemo(() => {
    const previewDocument = { ...document, elements: [...document.elements] };
    if (draft && !draft.cancelled) {
      for (const elementId of draft.elementIds) {
        const preview = previewDragPosition(draft, elementId);
        const layout = previewDocument.elements.find((entry) => entry.id === elementId);
        if (preview && layout) {
          layout.x = preview.x;
          layout.y = preview.y;
        }
      }
    }
    return applyDocumentLayoutToAdElements(resolvedElements, previewDocument);
  }, [document, draft, resolvedElements]);

  const hitTargets = useMemo(() => toHitTargets(displayArtwork), [displayArtwork]);

  const syncRegistry = useCallback((registry: CommandRegistry) => {
    setUndoDepth(registry.undoDepth);
    setRedoDepth(registry.redoDepth);
  }, []);

  const beginPointerSession = useCallback((event: ReactPointerEvent) => {
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Cypress and other test runners may synthesize pointer events without an active pointer.
    }
    pointerSessionRef.current = {
      pointerId: event.pointerId,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
    };
  }, []);

  const endPointerSession = useCallback((event: ReactPointerEvent) => {
    pointerSessionRef.current = null;
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Ignore release failures when capture was never established.
    }
  }, []);

  const clientDeltaSinceLastMove = useCallback((event: ReactPointerEvent) => {
    const session = pointerSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) {
      return null;
    }
    const delta = {
      x: event.clientX - session.lastClientX,
      y: event.clientY - session.lastClientY,
    };
    pointerSessionRef.current = {
      ...session,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
    };
    return delta;
  }, []);

  const stagePointFromEvent = useCallback(
    (clientX: number, clientY: number) => {
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) {
        return { x: 0, y: 0 };
      }
      const screen = { x: clientX - rect.left, y: clientY - rect.top };
      return screenToStage(screen, viewport);
    },
    [viewport],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (event.button !== 0) {
        return;
      }
      if (event.altKey) {
        panStartRef.current = {
          x: event.clientX,
          y: event.clientY,
          panX: viewport.panX,
          panY: viewport.panY,
        };
        beginPointerSession(event);
        return;
      }

      const stagePoint = stagePointFromEvent(event.clientX, event.clientY);
      const picked = pickTopmostAtPoint(hitTargets, stagePoint);
      if (picked) {
        onSelectElement(picked);
        const layout = document.elements.find((entry) => entry.id === picked);
        const startPositions: Record<string, { x: number; y: number }> = {};
        if (layout) {
          startPositions[picked] = { x: layout.x, y: layout.y };
        }
        const nextDraft = beginDrag([picked], startPositions);
        draftRef.current = nextDraft;
        setDraft(nextDraft);
        beginPointerSession(event);
      } else {
        onSelectElement(null);
      }
    },
    [
      beginPointerSession,
      document.elements,
      hitTargets,
      onSelectElement,
      stagePointFromEvent,
      viewport.panX,
      viewport.panY,
    ],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent) => {
      if (panStartRef.current) {
        const delta = {
          x: event.clientX - panStartRef.current.x,
          y: event.clientY - panStartRef.current.y,
        };
        setViewport({
          panX: panStartRef.current.panX + delta.x,
          panY: panStartRef.current.panY + delta.y,
          zoom: viewport.zoom,
        });
        return;
      }

      if (draftRef.current && !draftRef.current.cancelled) {
        const delta = clientDeltaSinceLastMove(event);
        if (delta && (delta.x !== 0 || delta.y !== 0)) {
          setDraft((current) => {
            if (!current || current.cancelled) {
              return current;
            }
            const updated = updateDragFromScreenDelta(current, delta, viewport.zoom);
            draftRef.current = updated;
            return updated;
          });
        }
      }
    },
    [clientDeltaSinceLastMove, viewport.zoom],
  );

  const finishPointer = useCallback(
    (event: ReactPointerEvent) => {
      panStartRef.current = null;
      endPointerSession(event);

      const activeDraft = draftRef.current;
      if (activeDraft && !activeDraft.cancelled) {
        const committed = commitDragGesture(registryRef.current, document, activeDraft);
        if (committed) {
          onElementsChange(applyDocumentLayoutToAdElements(elements, committed.document));
          syncRegistry(committed.registry);
          registryRef.current = committed.registry;
        }
      }
      draftRef.current = null;
      setDraft(null);
    },
    [document, elements, endPointerSession, onElementsChange, syncRegistry],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && draftRef.current) {
        const cancelled = cancelDrag(draftRef.current);
        draftRef.current = cancelled;
        setDraft(cancelled);
        pointerSessionRef.current = null;
        panStartRef.current = null;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        if (event.shiftKey) {
          const doc = documentFromEditorElements(elements, animation ?? null);
          const redone = registryRef.current.redo(doc);
          if (redone) {
            syncRegistry(registryRef.current);
            onElementsChange(applyDocumentLayoutToAdElements(elements, redone));
          }
          return;
        }
        const doc = documentFromEditorElements(elements, animation ?? null);
        const undone = registryRef.current.undo(doc);
        if (undone) {
          syncRegistry(registryRef.current);
          onElementsChange(applyDocumentLayoutToAdElements(elements, undone));
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [animation, elements, onElementsChange, syncRegistry]);

  const resetViewport = useCallback(() => {
    setViewport(fitFrameToViewport(frame, stageSize, viewportPadding));
  }, [frame, stageSize, viewportPadding]);

  const setZoom = useCallback((zoom: number) => {
    setViewport((current) => ({ ...current, zoom }));
  }, []);

  const panByScreen = useCallback((delta: { x: number; y: number }) => {
    setViewport((current) => ({
      ...current,
      panX: current.panX + delta.x,
      panY: current.panY + delta.y,
    }));
  }, []);

  const undo = useCallback(() => {
    const doc = documentFromEditorElements(elements, animation ?? null);
    const undone = registryRef.current.undo(doc);
    if (undone) {
      syncRegistry(registryRef.current);
      onElementsChange(applyDocumentLayoutToAdElements(elements, undone));
    }
  }, [animation, elements, onElementsChange, syncRegistry]);

  const redo = useCallback(() => {
    const doc = documentFromEditorElements(elements, animation ?? null);
    const redone = registryRef.current.redo(doc);
    if (redone) {
      syncRegistry(registryRef.current);
      onElementsChange(applyDocumentLayoutToAdElements(elements, redone));
    }
  }, [animation, elements, onElementsChange, syncRegistry]);

  const selectedIds = selectedElementId ? [selectedElementId] : [];
  const selectionBounds = combinedAxisAlignedBounds(hitTargets, selectedIds);

  return {
    stageRef,
    viewport,
    displayArtwork,
    selectionBounds,
    undoDepth,
    redoDepth,
    clipToFrame,
    setZoom,
    resetViewport,
    panByScreen,
    undo,
    redo,
    onPointerDown,
    onPointerMove,
    onPointerUp: finishPointer,
    onPointerCancel: finishPointer,
  };
}
